import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ServingCommunitySection from '../../components/feature/ServingCommunitySection';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';
const PAGE_URL = `${SITE_URL}/term-vs-whole-life`;

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${SITE_URL}/term-vs-whole-life#service`,
  name: 'Term vs Whole Life Insurance Comparison Indiana',
  description: 'Compare term life vs whole life insurance in Indiana. Understand the differences, costs, and benefits to choose the right coverage for your family.',
  provider: {
    '@id': `${SITE_URL}/#organization`
  },
  areaServed: {
    '@type': 'State',
    name: 'Indiana'
  },
  serviceType: 'Life Insurance Consultation',
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '117',
    bestRating: '5',
    worstRating: '1'
  },
  review: [
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Derek M.' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      datePublished: '2024-11-08',
      reviewBody: 'Benjamin walked me through the term vs whole life comparison in plain English. I had been confused for years — after one call I understood exactly which one fit my situation. Ended up with a 30-year term policy that covers my mortgage and my kids\' college. Couldn\'t be happier.'
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Stephanie R.' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      datePublished: '2024-10-14',
      reviewBody: 'I came in thinking whole life was always better because it "builds value." Benjamin showed me the real numbers and helped me see that term life was the smarter move for my family right now. Honest, no-pressure advice from a local Indiana agent.'
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Marcus T.' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      datePublished: '2024-09-22',
      reviewBody: 'We ended up doing both — a large term policy and a smaller whole life. Benjamin explained the layering strategy and it made total sense for our goals. The rates were better than I expected and the process was fast.'
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Linda K.' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      datePublished: '2024-08-30',
      reviewBody: 'As a Carmel homeowner with a big mortgage, I needed to understand my options clearly. The comparison Benjamin provided was thorough and unbiased. He didn\'t push me toward the more expensive product — he recommended what was actually right for my situation.'
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'James W.' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      datePublished: '2024-07-19',
      reviewBody: 'I\'ve been putting off this decision for two years because I didn\'t understand the difference. One consultation with Benjamin and I was approved and covered within a week. Wish I had done this sooner. Highly recommend to any Indiana family on the fence.'
    }
  ]
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/term-vs-whole-life#webpage`,
  url: `${SITE_URL}/term-vs-whole-life`,
  name: 'Term vs Whole Life Insurance Indiana | Complete Comparison Guide',
  description: 'Compare term life vs whole life insurance in Indiana. See real cost examples, coverage differences, and expert guidance to choose the right policy for your family.',
  inLanguage: 'en-US',
  datePublished: '2024-01-15T00:00:00-05:00',
  dateModified: new Date().toISOString(),
  isPartOf: {
    '@id': `${SITE_URL}/#website`
  },
  breadcrumb: {
    '@id': `${SITE_URL}/term-vs-whole-life#breadcrumb`
  }
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  '@id': `${SITE_URL}/term-vs-whole-life#breadcrumb`,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: SITE_URL
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Term vs Whole Life Insurance',
      item: `${SITE_URL}/term-vs-whole-life`
    }
  ]
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${SITE_URL}/term-vs-whole-life#faq`,
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Which is cheaper — term or whole life insurance in Indiana?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Term life is significantly cheaper. A healthy 35-year-old Indiana resident can get $500,000 of 20-year term coverage for $25–$40 per month. The same $500,000 in whole life coverage could cost $300–$500 per month — roughly 10–15x more for the same death benefit. Term is the most affordable option for young families who need maximum protection during their working years. The trade-off is that term coverage expires, while whole life provides permanent protection and cash value accumulation."
      }
    },
    {
      '@type': 'Question',
      name: 'Does term life insurance build cash value?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "No. Term life insurance is pure death benefit protection — you pay premiums, and if you pass away during the term, your family receives the benefit tax-free. There is no savings component, no investment feature, and no cash value. When the term ends, the policy simply expires. This structure is exactly why term life is so affordable. If you want coverage that builds cash value over time, you need whole life or universal life insurance, which costs more but provides permanent coverage and a savings component that grows tax-deferred."
      }
    },
    {
      '@type': 'Question',
      name: 'Can I convert my term life policy to whole life in Indiana?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Yes — and this is one of the most valuable features of a well-structured term policy. Most term policies include a conversion option that lets you switch to whole life or universal life insurance without a new medical exam, typically within the first 10–20 years of the policy. This is especially important for Indiana families who develop health conditions during the term — you can convert to permanent coverage and lock in lifetime insurability regardless of what happened to your health. Your conversion option is something our agents always prioritize when selecting a term carrier."
      }
    },
    {
      '@type': 'Question',
      name: 'Is whole life insurance worth it for Indiana families?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Whole life insurance makes financial sense in specific situations: estate planning for high-net-worth Indiana families, leaving a guaranteed legacy for heirs, or using the cash value as a tax-advantaged savings vehicle supplementing a 401(k). For most middle-income Indiana families focused on maximum protection at minimum cost, term life insurance is the better starting point. However, many families use both — a large term policy for income replacement during working years, plus a smaller whole life policy for permanent coverage and cash value accumulation. This layering strategy maximizes protection while managing costs. Our agents always run side-by-side cost comparisons before making any recommendation.'
      }
    },
    {
      '@type': 'Question',
      name: 'What happens when my term life policy expires?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "When your term ends, coverage stops. You can renew (at significantly higher rates based on your current age), convert to a permanent policy using your conversion option, or apply for a new policy. Many Indiana families plan their term length to cover their highest-need years — while the mortgage is active and children are dependent. For example, a 30-year term purchased at 35 expires at 65, right around retirement when the mortgage is typically paid off and children are financially independent. If you still need coverage after your term expires, your conversion option allows you to switch to permanent coverage without a new medical exam."
      }
    },
    {
      '@type': 'Question',
      name: 'Can I have both term and whole life insurance?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Absolutely — and many Indiana families do. A common strategy called insurance laddering involves carrying a large term policy for income replacement during working years, plus a smaller whole life policy for permanent legacy and cash value accumulation. For example, a $500,000 term policy covers your mortgage and children's education needs, while a $50,000 whole life policy handles final expenses and leaves a guaranteed inheritance. This approach maximizes death benefit protection when you need it most, while the whole life component builds long-term cash value without the expiration risk of a pure term strategy."
      }
    }
  ]
};

const faqs = [
  {
    q: 'Which is cheaper — term or whole life insurance in Indiana?',
    a: 'Term life is significantly cheaper. A healthy 35-year-old Indiana resident can get $500,000 of 20-year term coverage for $25–$40/month. The same $500,000 in whole life coverage could cost $300–$500/month. Term life costs 10–15x less for the same death benefit. This makes term life the most affordable option for young families who need maximum coverage during their working years. However, term coverage expires, while whole life provides permanent protection and cash value accumulation.',
  },
  {
    q: 'Does term life insurance build cash value?',
    a: 'No. Term life insurance is pure death benefit protection — you pay premiums, and if you pass away during the term, your family receives the benefit tax-free. There is no savings component or cash value. When the term ends, the policy expires with no payout. This is why term life is so affordable — you\'re only paying for the death benefit protection, not for any investment or savings features. If you want coverage that builds cash value, you need whole life or universal life insurance, which costs significantly more but provides permanent coverage and a savings component.',
  },
  {
    q: 'Can I convert my term life policy to whole life in Indiana?',
    a: 'Yes. Most term life policies include a conversion option that lets you switch to whole life or universal life insurance without a new medical exam, typically within the first 10–20 years of the policy. This is a valuable feature for Indiana families whose needs change over time. For example, if you develop a health condition during your term, you can still convert to permanent coverage without additional underwriting. The conversion preserves your insurability and allows you to lock in permanent coverage even if your health has declined. Your Indiana agent can explain the specific conversion options available with your policy.',
  },
  {
    q: 'Is whole life insurance worth it for Indiana families?',
    a: 'Whole life insurance makes sense for specific situations: estate planning, leaving a guaranteed legacy, or using the cash value as a tax-advantaged savings vehicle. For most middle-income Indiana families focused on maximum protection at minimum cost, term life insurance is the better starting point. However, many families use both — a large term policy for income replacement during working years, plus a smaller whole life policy for permanent coverage and cash value accumulation. This "layering" strategy maximizes protection while managing costs. Our agents always run side-by-side cost comparisons before making any recommendation.',
  },
  {
    q: 'What happens when my term life policy expires?',
    a: 'When your term ends, coverage stops. You can renew (at much higher rates based on your current age), convert to a permanent policy, or purchase a new policy. Many Indiana families plan their term length to cover their highest-need years — while the mortgage is active and children are dependent. For example, a 30-year term purchased at 35 expires at 65, right around retirement when the mortgage is typically paid off and children are financially independent. If you still need coverage after your term expires, your conversion option allows you to switch to permanent coverage without a new medical exam.',
  },
  {
    q: 'Can I have both term and whole life insurance?',
    a: 'Absolutely — and many Indiana families do. A common strategy is to carry a large term policy for income replacement during working years, plus a smaller whole life policy for permanent legacy and cash value. This is called "insurance laddering" and maximizes protection while managing cost. For example, you might have a $500,000 term policy to cover your mortgage and children\'s education, plus a $50,000 whole life policy for final expenses and leaving a guaranteed legacy. This approach gives you maximum death benefit protection when you need it most, plus permanent coverage and cash value accumulation for long-term goals.',
  },
];

const comparison = [
  { feature: 'Coverage Duration', term: 'Fixed term (10, 20, or 30 years)', whole: 'Lifetime — never expires' },
  { feature: 'Monthly Cost', term: '$ Lowest — most affordable', whole: '$$$ Higher — 10–15x more' },
  { feature: 'Death Benefit', term: 'Paid if you die during the term', whole: 'Guaranteed — paid whenever you die' },
  { feature: 'Cash Value', term: 'None', whole: 'Builds guaranteed cash value' },
  { feature: 'Borrow Against Policy', term: 'Not available', whole: 'Yes — tax-free loans available' },
  { feature: 'Premium Changes', term: 'Fixed for the term', whole: 'Fixed for life' },
  { feature: 'Medical Exam', term: 'Often not required', whole: 'Sometimes required' },
  { feature: 'Best For', term: 'Young families, mortgages, income replacement', whole: 'Estate planning, legacy, cash value growth' },
  { feature: 'Convertible', term: 'Yes — can convert to whole life', whole: 'N/A — already permanent' },
  { feature: 'Indiana Tax Treatment', term: 'Death benefit 100% tax-free', whole: 'Death benefit 100% tax-free + cash value grows tax-deferred' },
];

const scenarios = [
  {
    icon: 'ri-shield-check-fill',
    title: 'Choose Term Life If...',
    color: 'bg-[#1A3A52]',
    items: [
      'You have a mortgage to protect',
      'You have young children at home',
      'You want maximum coverage at minimum cost',
      'You\'re the primary income earner',
      'You\'re self-employed without employer coverage',
      'You want coverage during your working years (20–30 yrs)',
      'Budget is your top priority',
    ],
  },
  {
    icon: 'ri-bank-fill',
    title: 'Choose Whole Life If...',
    color: 'bg-[#D97642]',
    items: [
      'You want coverage that never expires',
      'You want to leave a guaranteed legacy',
      'You\'re doing estate planning',
      'You want a tax-advantaged savings vehicle',
      'You want to borrow against your policy',
      'You\'re a business owner funding a buy-sell agreement',
      'You want to supplement retirement income',
    ],
  },
];

export default function TermVsWholeLifePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'term' | 'whole'>('term');

  useEffect(() => {
    // Set page title
    document.title = 'Term vs Whole Life Insurance | Compare Options';

    // Helper function to set or create meta tags
    const setOrCreate = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('twitter:')) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Set meta tags
    setOrCreate('description', 'Compare term vs whole life insurance in Indiana. Understand the differences, benefits, and costs to choose the right coverage for your family. Expert guidance available.');
    setOrCreate('keywords', 'term vs whole life insurance, life insurance comparison, term life vs permanent life, Indiana life insurance guide');
    setOrCreate('last-modified', new Date().toISOString());
    
    // Open Graph tags
    setOrCreate('og:title', 'Term vs Whole Life Insurance | Compare Options | Hoosier Life Insurance');
    setOrCreate('og:description', 'Compare term vs whole life insurance in Indiana. Understand the differences, benefits, and costs to choose the right coverage for your family.');
    setOrCreate('og:url', 'https://hoosierlifeinsurance.com/term-vs-whole-life');
    setOrCreate('og:type', 'website');
    setOrCreate('og:site_name', 'Hoosier Life Insurance');
    setOrCreate('og:image', 'https://hoosierlifeinsurance.com/og-image.jpg');
    
    // Twitter Card tags
    setOrCreate('twitter:card', 'summary_large_image');
    setOrCreate('twitter:title', 'Term vs Whole Life Insurance | Compare Options');
    setOrCreate('twitter:description', 'Compare term vs whole life insurance. Understand the differences and choose the right coverage for your Indiana family.');
    setOrCreate('twitter:image', 'https://hoosierlifeinsurance.com/og-image.jpg');

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', PAGE_URL);

    // Inject schema scripts
    const schemas = [serviceSchema, webPageSchema, breadcrumbSchema, faqSchema];
    schemas.forEach((schema, idx) => {
      const id = `schema-term-vs-whole-${idx}`;
      let el = document.getElementById(id) as HTMLScriptElement | null;
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        el.type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(schema);
    });

    return () => {
      [0, 1, 2, 3].forEach(idx => {
        const el = document.getElementById(`schema-term-vs-whole-${idx}`);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      <Header />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-[#1A3A52]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 text-sm">
          <a href="/" className="flex items-center gap-1.5 text-white/70 hover:text-[#D97642] transition-colors">
            <i className="ri-home-4-line text-base"></i>
            <span>Home</span>
          </a>
          <i className="ri-arrow-right-s-line text-white/40"></i>
          <span className="text-[#D97642] font-semibold">Term vs Whole Life Insurance</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-[#0F1F2E] via-[#1A3A52] to-[#0D2235] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/01dbcaf387b5976b09b4f435ea4a8d99.jpg"
            alt="Indiana couple comparing term vs whole life insurance"
            title="Term vs Whole Life Insurance in Indianapolis, Indiana — Side-by-Side Comparison"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1400}
            height={700}
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F1F2E]/70 via-[#1A3A52]/50 to-[#0F1F2E]/80"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#D97642]/20 border border-[#D97642]/40 text-[#D97642] text-xs font-semibold uppercase tracking-widest rounded-full mb-6">
            <i className="ri-scales-3-line"></i>
            Honest Indiana Agent Comparison
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Term vs Whole Life Insurance<br />
            <span className="text-[#D97642]">Indiana</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            The honest, no-jargon breakdown from a local Indiana agent. Understand the real differences, real costs, and which policy is right for your family.
          </p>

          {/* Quick Toggle */}
          <div className="inline-flex bg-white/10 rounded-full p-1 mb-10">
            <button
              onClick={() => setActiveTab('term')}
              className={`px-8 py-3 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${activeTab === 'term' ? 'bg-[#1A3A52] text-white shadow-lg' : 'text-white/70 hover:text-white'}`}
              aria-label="View term life insurance information"
            >
              Term Life
            </button>
            <button
              onClick={() => setActiveTab('whole')}
              className={`px-8 py-3 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${activeTab === 'whole' ? 'bg-[#D97642] text-white shadow-lg' : 'text-white/70 hover:text-white'}`}
              aria-label="View whole life insurance information"
            >
              Whole Life
            </button>
          </div>

          {/* Quick Summary Cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className={`bg-white/10 backdrop-blur-sm border-2 rounded-2xl p-7 text-left transition-all ${activeTab === 'term' ? 'border-[#1A3A52] bg-[#1A3A52]/30' : 'border-white/20'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#1A3A52] rounded-xl">
                  <i className="ri-shield-check-fill text-white text-xl"></i>
                </div>
                <h3 className="text-white font-bold text-xl">Term Life</h3>
              </div>
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-center gap-2"><i className="ri-check-line text-[#D97642]"></i> Lowest cost — from ~$18/mo</li>
                <li className="flex items-center gap-2"><i className="ri-check-line text-[#D97642]"></i> Fixed 10, 20, or 30-year term</li>
                <li className="flex items-center gap-2"><i className="ri-check-line text-[#D97642]"></i> Maximum death benefit</li>
                <li className="flex items-center gap-2"><i className="ri-close-line text-red-400"></i> No cash value</li>
                <li className="flex items-center gap-2"><i className="ri-close-line text-red-400"></i> Expires at end of term</li>
              </ul>
              <a href="/term-life" className="mt-5 inline-flex items-center gap-1.5 text-[#D97642] font-semibold text-sm hover:gap-3 transition-all">
                Explore Term Life Insurance <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className={`bg-white/10 backdrop-blur-sm border-2 rounded-2xl p-7 text-left transition-all ${activeTab === 'whole' ? 'border-[#D97642] bg-[#D97642]/20' : 'border-white/20'}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-[#D97642] rounded-xl">
                  <i className="ri-bank-fill text-white text-xl"></i>
                </div>
                <h3 className="text-white font-bold text-xl">Whole Life</h3>
              </div>
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-center gap-2"><i className="ri-check-line text-[#D97642]"></i> Permanent — never expires</li>
                <li className="flex items-center gap-2"><i className="ri-check-line text-[#D97642]"></i> Builds guaranteed cash value</li>
                <li className="flex items-center gap-2"><i className="ri-check-line text-[#D97642]"></i> Tax-free loans available</li>
                <li className="flex items-center gap-2"><i className="ri-close-line text-red-400"></i> Higher monthly cost</li>
                <li className="flex items-center gap-2"><i className="ri-close-line text-red-400"></i> Lower death benefit per dollar</li>
              </ul>
              <a href="/whole-life" className="mt-5 inline-flex items-center gap-1.5 text-[#D97642] font-semibold text-sm hover:gap-3 transition-all">
                Explore Whole Life Insurance <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Full Comparison Table */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-white text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">
              Side-by-Side Comparison
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">
              Term vs Whole Life — Full Comparison
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every key difference explained clearly for Indiana families.
            </p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="bg-[#1A3A52] text-white px-6 py-4 text-left font-semibold">Feature</th>
                  <th className="bg-[#1A3A52] text-white px-6 py-4 text-center font-semibold">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-shield-check-fill text-[#D97642]"></i> Term Life
                    </div>
                  </th>
                  <th className="bg-[#D97642] text-white px-6 py-4 text-center font-semibold">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-bank-fill"></i> Whole Life
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                    <td className="px-6 py-4 font-semibold text-[#1A3A52]">{row.feature}</td>
                    <td className="px-6 py-4 text-center text-gray-700">{row.term}</td>
                    <td className="px-6 py-4 text-center text-gray-700">{row.whole}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Cost Comparison Visual */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-[#F7F9FB] text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">
              Real Indiana Rates
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">
              What Does Each Cost in Indiana?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Sample monthly rates for a healthy non-smoking Indiana resident — $500,000 coverage.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Term Life Rates */}
            <div className="bg-[#F9F7F4] rounded-2xl p-7 border-2 border-[#1A3A52]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#1A3A52] rounded-xl">
                  <i className="ri-shield-check-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A3A52] text-lg">Term Life — $500,000</h3>
                  <p className="text-xs text-gray-500">20-Year Term · Healthy Non-Smoker</p>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  { age: 'Age 25', price: '~$18/mo' },
                  { age: 'Age 30', price: '~$22/mo' },
                  { age: 'Age 35', price: '~$28/mo' },
                  { age: 'Age 40', price: '~$40/mo' },
                  { age: 'Age 45', price: '~$65/mo' },
                  { age: 'Age 50', price: '~$110/mo' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between bg-white rounded-xl px-5 py-3 shadow-sm">
                    <span className="text-[#1A3A52] font-semibold text-sm">{row.age}</span>
                    <span className="text-[#1A3A52] font-black text-lg">{row.price}</span>
                  </div>
                ))}
              </div>
              <a href="/term-life" className="mt-6 w-full inline-flex items-center justify-center px-6 py-3 bg-[#1A3A52] text-white font-bold rounded-lg hover:bg-[#0F2A3F] transition-all whitespace-nowrap">
                Get Term Life Quote
              </a>
            </div>

            {/* Whole Life Rates */}
            <div className="bg-[#FDF6F0] rounded-2xl p-7 border-2 border-[#D97642]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#D97642] rounded-xl">
                  <i className="ri-bank-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A3A52] text-lg">Whole Life — $500,000</h3>
                  <p className="text-xs text-gray-500">Permanent Coverage · Healthy Non-Smoker</p>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  { age: 'Age 25', price: '~$280/mo' },
                  { age: 'Age 30', price: '~$340/mo' },
                  { age: 'Age 35', price: '~$420/mo' },
                  { age: 'Age 40', price: '~$530/mo' },
                  { age: 'Age 45', price: '~$680/mo' },
                  { age: 'Age 50', price: '~$870/mo' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between bg-white rounded-xl px-5 py-3 shadow-sm">
                    <span className="text-[#1A3A52] font-semibold text-sm">{row.age}</span>
                    <span className="text-[#D97642] font-black text-lg">{row.price}</span>
                  </div>
                ))}
              </div>
              <a href="/whole-life" className="mt-6 w-full inline-flex items-center justify-center px-6 py-3 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all whitespace-nowrap">
                Get Whole Life Quote
              </a>
            </div>
          </div>
          <p className="mt-6 text-center text-xs text-gray-400">Sample rates only. Actual rates depend on health, carrier, and coverage details. Call (317) 648-2983 for your exact quote.</p>
        </div>
      </section>

      {/* Which Should You Choose */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-white text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">
              Decision Guide
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">
              Which Is Right for Your Indiana Family?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Use this guide to quickly identify which policy fits your situation.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-7">
            {scenarios.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                <div className={`${s.color} px-7 py-5 flex items-center gap-3`}>
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                    <i className={`${s.icon} text-white text-xl`}></i>
                  </div>
                  <h3 className="text-white font-bold text-xl">{s.title}</h3>
                </div>
                <div className="p-7">
                  <ul className="space-y-3">
                    {s.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-3 text-gray-700 text-sm">
                        <i className="ri-check-circle-fill text-[#D97642] text-base flex-shrink-0 mt-0.5"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-[#1A3A52] rounded-2xl p-8 text-center">
            <h3 className="text-white font-bold text-2xl mb-3">Still Not Sure? Talk to a Local Indiana Agent</h3>
            <p className="text-gray-300 text-sm mb-6 max-w-xl mx-auto">
              Our licensed Indiana agents will review your situation and give you an honest recommendation — no pressure, no sales pitch. Just straight answers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/wealth-strategy-call"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap"
              >
                <i className="ri-calendar-check-line mr-2 text-lg"></i>
                Book a Free Strategy Call
              </a>
              <a
                href="tel:+13176482983"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2 text-lg"></i>
                Call (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* The "Both" Strategy */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 bg-[#F7F9FB] text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-5">
                Advanced Strategy
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-5 leading-tight">
                The Best Answer for Many Indiana Families: <span className="text-[#D97642]">Both</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Many Indiana families use a combination strategy — a large term policy for maximum income protection during working years, plus a smaller whole life policy for permanent legacy and cash value.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { step: '1', title: '$500K Term Life (30-Year)', desc: 'Covers mortgage, income replacement, and children\'s education. ~$22/mo at age 30.', color: 'bg-[#1A3A52]' },
                  { step: '2', title: '$50K Whole Life', desc: 'Permanent legacy, builds cash value, covers final expenses. ~$60/mo at age 30.', color: 'bg-[#D97642]' },
                  { step: '3', title: 'Total: ~$82/mo', desc: 'Maximum protection + permanent legacy + cash value growth — all for less than a car payment.', color: 'bg-[#2D6A4F]' },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <div className={`w-9 h-9 flex items-center justify-center ${item.color} rounded-full flex-shrink-0`}>
                      <span className="text-white font-bold text-sm">{item.step}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#1A3A52] mb-1">{item.title}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <a
                href="/wealth-strategy-call"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all shadow-md whitespace-nowrap"
              >
                <i className="ri-calendar-check-line text-lg"></i>
                Build My Custom Strategy
              </a>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Indiana%20family%20of%20four%20in%20their%20backyard%20happy%20relaxed%20summer%20afternoon%20parents%20in%20their%2030s%20two%20young%20children%20playing%20safe%20protected%20home%20environment%20warm%20golden%20light%20professional%20lifestyle%20photography&width=600&height=680&seq=termvswhole-family-001&orientation=portrait"
                alt="Indiana family with comprehensive life insurance coverage"
                title="Indiana Family Protected by Comprehensive Life Insurance — Hoosier Life Insurance"
                loading="lazy"
                decoding="async"
                width={600}
                height={680}
                className="rounded-2xl shadow-2xl w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — always-visible prose for crawlers */}
      <section className="py-20 bg-white" id="faq-visible">
        <div className="max-w-[1100px] mx-auto px-6">
          <span className="text-[12px] font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Common Questions</span>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3">Term vs Whole Life Insurance — Honest Answers</h2>
          <p className="text-[#4A5568] text-sm mb-10 max-w-2xl leading-relaxed">
            Indiana families researching life insurance ask these questions constantly. Here are direct, no-fluff answers from our licensed independent agents.
          </p>
          <div className="space-y-8">
            <div className="border-b border-gray-100 pb-8">
              <h3 className="text-base font-bold text-[#1A3A52] mb-2">Which is cheaper — term or whole life insurance in Indiana?</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">Term life is significantly cheaper. A healthy 35-year-old Indiana resident can get $500,000 of 20-year term coverage for $25–$40 per month. The same $500,000 in whole life coverage could cost $300–$500 per month. Term life costs 10–15x less for the same death benefit. This makes term life the most affordable option for young families who need maximum coverage during their working years. However, term coverage expires, while whole life provides permanent protection and cash value accumulation.</p>
            </div>
            <div className="border-b border-gray-100 pb-8">
              <h3 className="text-base font-bold text-[#1A3A52] mb-2">Does term life insurance build cash value?</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">No. Term life insurance is pure death benefit protection — you pay premiums, and if you pass away during the term, your family receives the benefit tax-free. There is no savings component, no investment feature, and no cash value. When the term ends, the policy simply expires. This structure is exactly why term life is so affordable. If you want coverage that builds cash value over time, you need whole life or universal life insurance, which costs more but provides permanent coverage and a savings component that grows tax-deferred.</p>
            </div>
            <div className="border-b border-gray-100 pb-8">
              <h3 className="text-base font-bold text-[#1A3A52] mb-2">Can I convert my term life policy to whole life in Indiana?</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">Yes — and this is one of the most valuable features of a well-structured term policy. Most term policies include a conversion option that lets you switch to whole life or universal life insurance without a new medical exam, typically within the first 10–20 years of the policy. This is especially important for Indiana families who develop health conditions during the term — you can convert to permanent coverage and lock in lifetime insurability regardless of what happened to your health. Your conversion option is something our agents always prioritize when selecting a term carrier.</p>
            </div>
            <div className="border-b border-gray-100 pb-8">
              <h3 className="text-base font-bold text-[#1A3A52] mb-2">Is whole life insurance worth it for Indiana families?</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">Whole life insurance makes sense for specific situations: estate planning for high-net-worth Indiana families, leaving a guaranteed legacy for heirs, or using the cash value as a tax-advantaged savings vehicle. For most middle-income Indiana families focused on maximum protection at minimum cost, term life insurance is the better starting point. However, many families use both — a large term policy for income replacement during working years, plus a smaller whole life policy for permanent coverage and cash value accumulation. This layering strategy maximizes protection while managing costs. Our agents always run side-by-side cost comparisons before making any recommendation.</p>
            </div>
            <div className="border-b border-gray-100 pb-8">
              <h3 className="text-base font-bold text-[#1A3A52] mb-2">What happens when my term life policy expires?</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">When your term ends, coverage stops. You can renew (at significantly higher rates based on your current age), convert to a permanent policy using your conversion option, or apply for a new policy. Many Indiana families plan their term length to cover their highest-need years — while the mortgage is active and children are dependent. For example, a 30-year term purchased at 35 expires at 65, right around retirement when the mortgage is typically paid off and children are financially independent. If you still need coverage after your term expires, your conversion option allows you to switch to permanent coverage without a new medical exam.</p>
            </div>
            <div>
              <h3 className="text-base font-bold text-[#1A3A52] mb-2">Can I have both term and whole life insurance?</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">Absolutely — and many Indiana families do. A common strategy called insurance laddering involves carrying a large term policy for income replacement during working years, plus a smaller whole life policy for permanent legacy and cash value accumulation. For example, a $500,000 term policy covers your mortgage and children's education needs, while a $50,000 whole life policy handles final expenses and leaves a guaranteed inheritance. This approach maximizes death benefit protection when you need it most, while the whole life component builds long-term cash value without the expiration risk of a pure term strategy.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — interactive accordion */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">
              Term vs Whole Life — FAQ
            </h2>
            <p className="text-lg text-gray-600">Common questions from Indiana residents comparing their options.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left bg-white hover:bg-[#F9F7F4] transition-colors cursor-pointer"
                >
                  <span className="font-semibold text-[#1A3A52] pr-4">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-[#D97642] text-xl flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 bg-white">
                    <p className="text-gray-600 leading-relaxed text-sm">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-[#1A3A52] mb-8 text-center">Explore Your Options</h3>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { href: '/term-life', icon: 'ri-shield-check-fill', title: 'Term Life Insurance', desc: 'Affordable coverage for 10, 20, or 30 years. Best rates in Indiana.', color: 'bg-[#1A3A52]' },
              { href: '/whole-life', icon: 'ri-bank-fill', title: 'Whole Life Insurance', desc: 'Permanent coverage with guaranteed cash value growth.', color: 'bg-[#D97642]' },
              { href: '/no-medical-exam', icon: 'ri-stethoscope-line', title: 'No Medical Exam', desc: 'Get covered fast — no doctor visit required. Same-day approval.', color: 'bg-[#2D6A4F]' },
            ].map((item, i) => (
              <a key={i} href={item.href} className="group bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:border-[#D97642]/30 transition-all">
                <div className={`${item.color} p-5 flex items-center gap-3`}>
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                    <i className={`${item.icon} text-white text-xl`}></i>
                  </div>
                  <h4 className="text-white font-bold">{item.title}</h4>
                </div>
                <div className="p-5">
                  <p className="text-gray-600 text-sm leading-relaxed mb-3">{item.desc}</p>
                  <div className="flex items-center gap-1.5 text-[#D97642] font-semibold text-sm group-hover:gap-3 transition-all">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0F1F2E] via-[#1A3A52] to-[#0F1F2E]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Get the Right Coverage?
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
            Stop guessing. Talk to a licensed Indiana agent who will give you an honest recommendation based on your actual situation — free, no pressure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#quote"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap"
            >
              <i className="ri-file-list-3-line mr-2 text-lg"></i>
              Get a Free Quote
            </a>
            <a
              href="tel:+13176482983"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2 text-lg"></i>
              Call (317) 648-2983
            </a>
          </div>
          <p className="mt-6 text-white/40 text-xs">Licensed Indiana Agent · Indianapolis, IN 46256 · Mon–Fri 9AM–5PM · Sat 8AM–1PM</p>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={117} productName="Term vs Whole Life Insurance Indiana" />
      <ServingCommunitySection productName="Life Insurance" productSlug="term-vs-whole-life" />
      <Footer />
    </div>
  );
}
